import React, { useState, useEffect } from 'react';
import { Camera, Check, Plus, Download } from 'lucide-react';
import api from '../api/axios';
import Sqids from 'sqids';
import { generateCompliantPassword, validatePassword, passwordPolicyHint } from '../utils/passwordPolicy';
import { escapeCsvCell } from '../utils/csv';

const EquipmentManager = () => {
  const [currentView, setCurrentView] = useState('list');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [password, setPassword] = useState('');
  const [ftpId, setFtpId] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [ftpPassword, setFtpPassword] = useState('');
  const [equipment, setEquipment] = useState([]);
  const [error, setError] = useState(null);
  const [userGroup, setUserGroup] = useState('');
  const [cdpList, setCdpList] = useState([]);
  const [operatorList, setOperatorList] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showFtpPassword, setShowFtpPassword] = useState(false);
  const sqids = new Sqids({
    alphabet: 'abcdefghijklmnopqrstuvwxyz@#%$&*0123456789',
    minLength: 10
  });

  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        setError(null);
        const response = await api.get('/webapi/devices');
        if (response.data) {
          const formattedEquipment = response.data.devices.map(device => ({
            ...device,
            status: device.status === 'Y' ? 'Active' : 'Inactive'
          }));
          
          setEquipment(formattedEquipment);
          setUserGroup(response.data.userGroup);
        }
      } catch (error) {
        console.error('Error fetching equipment:', error);
        setError('Failed to fetch equipment list. Please try again later.');
        setEquipment([]);
      }
    };

    if (currentView === 'list') {
      fetchEquipment();
    }
  }, [currentView]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      if (currentView !== 'details' || !userGroup) return;

      try {
        if (userGroup === 'ADMIN') {
          const response = await api.get('/webapi/listcdp', { params: { pvalue: selectedDevice.cdpPk } });
          if (response.data) {
            setCdpList(response.data);
            const selectedCdp = response.data.find(cdp => cdp.selected);
            if (selectedCdp && selectedDevice && selectedDevice.cdpPk !== selectedCdp.cdp_pk) {
              setSelectedDevice(prev => ({ ...prev, cdpPk: selectedCdp.cdp_pk }));
            }
          }
        } else if (userGroup === 'CDP') {
          // The cdpPk for a CDP user should likely come from the initial equipment list fetch,
          // not from the selected device, which might not be assigned yet.
          // Let's assume it's on the response from /webapi/devices
          const cdpPk = equipment.length > 0 ? equipment[0].cdpPk : null; // Placeholder logic
          if (cdpPk) {
            const response = await api.get('/webapi/listcdp', { params: { cdp_pk: cdpPk, pvalue: selectedDevice.operatorPk } });
            if (response.data) {
              setOperatorList(response.data);
              const selectedOp = response.data.find(op => op.selected);
              // We only set the operator_pk if the device is being viewed, not on initial load
              if (selectedOp && selectedDevice && selectedDevice.operatorPk !== selectedOp.operator_pk) {
                setSelectedDevice(prev => ({ ...prev, operatorPk: selectedOp.operator_pk }));
              }
            }
          }
        }
      } catch (err) {
        console.error('Error fetching dropdown data:', err);
        setError('Failed to fetch assignment options.');
      }
    };

    fetchDropdownData();
    // This effect should run when the view changes or the user context is established.
  }, [currentView, userGroup]); // Removed selectedDevice to break the loop


  const handleDeviceClick = (device) => {
    setSelectedDevice(device);
    setCurrentView('details');
    setFtpId(device.ftpId);
    setPassword('');
    setConfirmPassword('');
    setFtpPassword('');
  };

  const handleBack = () => {
    setCurrentView('list');
    setError(null);
    setSuccessMessage(null);
    setSelectedDevice(null);
  };

  const handleGeneratePassword = () => {
    const generatedPassword = generateCompliantPassword();
    setPassword(generatedPassword);
    setConfirmPassword(generatedPassword);
    setError(null);
  };

  const handleSave = async () => {
    setError(null);
    setSuccessMessage(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match!');
      return;
    }
    if (password) {
      const policyError = validatePassword(password);
      if (policyError) {
        setError(policyError);
        return;
      }
    }

    // Validation for new device
    if (!selectedDevice?.devicePk) {
      if (!selectedDevice.deviceSerialNo) {
        setError('Serial No. is required.');
        return;
      }
      if (!ftpId) {
        setError('FTP ID is required.');
        return;
      }
      if (!password) {
        setError('Password is required.');
        return;
      }
      if (userGroup === 'ADMIN' && !selectedDevice.cdpPk) {
        setError('Assigning a CDP account is required.');
        return;
      }
      // Add other required field checks for new device here
    }


    const deviceData = {
      ...selectedDevice,
      userPwd: password || null,
      userCPwd: confirmPassword || null,
      ftpId: ftpId,
      ftpPwd: ftpPassword || null,
      status: selectedDevice.status === 'Active' ? 'Y' : 'N',
      userLocked: selectedDevice.userLocked ?? 0,
    };

    try {

      let response;
      if (selectedDevice?.devicePk) { // Check if devicePk exists to determine if it's an existing device
        response = await api.put('/webapi/saveDevice', deviceData);
        setSuccessMessage('Device updated successfully!');
      } else { // No devicePk, so it's a new device
        response = await api.post('/webapi/saveDevice', deviceData);
        setSuccessMessage('New device added successfully!');
        // Assuming the API returns the newly created device object, update selectedDevice
        setSelectedDevice(response.data);
      }      
      
      setPassword('');
      setConfirmPassword('');
      setFtpPassword('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save device settings.');
      console.error('Error saving device:', err);
    }
  };

  const getFormLabel = () => {
    switch (userGroup) {
      case 'ADMIN':
        return 'Fundus camera registered in the inventory.';
      case 'CDP':
        return 'Fundus camera registered in your account.';
      case 'OPERATOR':
        return 'Fundus camera registered in your account.';
      default:
        return '';
    }
  };

  const getColumnHeader = () => {
    switch (userGroup) {
      case 'ADMIN':
        return 'Assigned to Partner';
      case 'CDP':
        return 'Operator';
      case 'OPERATOR':
        return 'Location';
      default:
        return '';
    }
  };

  const handleNewDevice = () => {
    setCurrentView('details');
    setSelectedDevice({ status: 'Active' }); // Default status for a new device
    setPassword('');
    setConfirmPassword('');
    setFtpId('');
    setFtpPassword('');
    setError(null);
    setSuccessMessage(null);
  };

  const handleDownloadCSV = () => {
    if (!equipment || equipment.length === 0) {
      setError('No devices to download.');
      return;
    }

    const headers = ['Device Serial No.', 'Username', 'Assigned To/Location', 'Assigned Operator', 'Status'];
    const csvContent = [
      headers.join(','),
      ...equipment.map(device => {
        const assignedToLoc = (userGroup === 'ADMIN' || userGroup === 'CDP') ? (device.assignedTo || '') : (device.location || '');
        return [
          escapeCsvCell(device.deviceSerialNo),
          escapeCsvCell(device.userId),
          escapeCsvCell(assignedToLoc),
          escapeCsvCell(device.assignedOperator),
          escapeCsvCell(device.status)
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `equipment_list_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (currentView === 'list') {
    return (
      <div className="min-h-screen ">
        <div className="max-w-5xl ">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Registered Equipment</h1>
            <p className="text-gray-600">{getFormLabel()}</p>
          </div>

                {/* Card Container */}
      
        {/* Card Header */}
        <div className="p-4 border-b flex gap-2">
          {userGroup === 'ADMIN' && (
            <button
              onClick={handleNewDevice}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              <span>Device</span>
            </button>
          )}
          <button
            onClick={handleDownloadCSV}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
          >
            <Download size={18} />
            <span>CSV</span>
          </button>
        </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
              <strong className="font-bold">Error: </strong>
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          <div className="bg-white rounded-lg shadow">
            <div className="grid grid-cols-6 gap-5 p-6 border-b border-gray-200 font-semibold text-gray-700">
              <div>Device Serial No.</div>
              <div>Username</div>
              <div>{getColumnHeader()}</div>
              {userGroup === 'ADMIN' && (
                    <div>
                      Assigned to Operator
                    </div>
                  )}

              <div className="text-right">STATUS</div>
              <div className="text-right">QR CODE</div>
            </div>

            {equipment.map((device, index) => (
              <div
                key={index}
                className="grid grid-cols-6 gap-5 p-6 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                
              >
                <div onClick={() => handleDeviceClick(device)} className="text-blue-600 font-medium">{device.deviceSerialNo}</div>
                <div onClick={() => handleDeviceClick(device)} className="text-gray-800">{device.userId}</div>
                <div className="text-gray-600">{userGroup === 'ADMIN' || userGroup === 'CDP'
                            ? device.assignedTo
                            : device.location}</div>

                {userGroup === 'ADMIN' && (
                        <div className="text-gray-600">
                            {device.assignedOperator}
                          </div>
                      )}

                <div className="text-right">
                  <span className="text-green-600 font-medium">{device.status}</span>
                </div>
                <div className="text-right">
                  <span className="text-green-600 font-medium"><a href={'/webapi/qrcode?Userpk='+encodeURIComponent(sqids.encode([device?.userPk]))} target="_blank" rel="noopener noreferrer">view</a></span>
                </div>
              </div>
            ))}
          </div>


          
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      <div className="max-w-4xl ">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Device Information</h1>
          <div className="flex items-center gap-2 text-sm">
            <span
              className="text-indigo-600 cursor-pointer hover:underline"
              onClick={handleBack}
            >
              Equipment List
            </span>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600">{selectedDevice?.devicePk ? 'Edit Device' : 'New Device'}</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-8">
          <div className="mb-8">
            {successMessage && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6" role="alert">
                <strong className="font-bold">Success: </strong>
                <span className="block sm:inline">{successMessage}</span>
              </div>
            )}
            {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">{error}</div>}
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-2xl font-bold text-gray-800">Device Information</h2>
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <Camera className="w-4 h-4 text-gray-500" />
              </div>
            </div>

            {userGroup === 'ADMIN' ? (
              <div className="mb-6">
                <label htmlFor="Device_SerialNo" className="block text-gray-700 mb-2">Serial No.</label>
                <input
                  required
                  type="text"
                  autoComplete="off"
                  className="w-full max-w-xl px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  id="Device_SerialNo"
                  name="Device_SerialNo"
                  value={selectedDevice?.deviceSerialNo || ''}
                  onChange={(e) => setSelectedDevice(prev => ({ ...prev, deviceSerialNo: e.target.value }))}
                />
              </div>
            ) : (
              <div className="mb-6">
                <span className="text-gray-700 font-semibold">Serial No.: </span>
                <span className="text-green-600 font-semibold">{selectedDevice?.deviceSerialNo}</span>
              </div>
            )}

            {userGroup === 'ADMIN' && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 mb-2">FTP ID</label>
                    <input
                      type="text"
                      name="FTP_id"
                      value={ftpId}
                      onChange={(e) => setFtpId(e.target.value)}
                      autoComplete="off"
                      className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="FTP ID"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2">FTP password</label>
                    <div className="relative">
                      <input
                        type={showFtpPassword ? 'text' : 'password'}
                        name="FTP_pwd"
                        value={ftpPassword}
                        onChange={(e) => setFtpPassword(e.target.value)}
                        autoComplete="off"
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="FTP password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowFtpPassword(!showFtpPassword)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showFtpPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="form-check mt-3 mb-2">
                  <input
                    className="form-check-input form-check-input-primary"
                    type="checkbox"
                    value="Y"
                    id="Acc_Active"
                    name="Status"
                    checked={selectedDevice?.status === 'Active'}
                    onChange={(e) => setSelectedDevice(prev => ({ ...prev, status: e.target.checked ? 'Active' : 'Inactive' }))}
                  />
                  <label className="form-check-label user-select-none ml-2" htmlFor="Acc_Active">
                    Active - <span className="text-sm">Device status</span>
                  </label>
                </div>
                <div className="form-check mb-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value="Y"
                    id="Acc_Locked"
                    name="UserLocked"
                    checked={selectedDevice?.userLocked === 1}
                    onChange={(e) => setSelectedDevice(prev => ({ ...prev, userLocked: e.target.checked ? 1 : 0 }))}
                  />
                  <label className="form-check-label user-select-none ml-2" htmlFor="Acc_Locked">
                    Locked - <span className="text-sm">Account locked</span>
                  </label>
                </div>
              </>
            )}

            {userGroup === 'OPERATOR' && (
              <div className="mb-4">
                <label htmlFor="user-Location" className="block text-gray-700 mb-2">Device assigned location</label>
                <input
                  type="text"
                  autoComplete="off"
                  className="w-full max-w-xl px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  id="user-Location"
                  name="Location"
                  value={selectedDevice?.location || ''}
                  onChange={(e) => setSelectedDevice(prev => ({ ...prev, location: e.target.value }))}
                />
              </div>
            )}

            {userGroup === 'ADMIN' && (
              <div className="mb-4">
                <label htmlFor="CDP" className="block text-gray-700 mb-2">Assign this device to a CDP Account.</label>
                <select
                  className="w-full max-w-xl p-3 border border-gray-300 rounded-lg text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  id="CDP"
                  name="CDP_pk_Assigned"
                  value={selectedDevice?.cdpPk || ''}
                  onChange={(e) => {
                    const newCdpPk = e.target.value;
                    // When CDP changes, reset the operator selection
                    setSelectedDevice(prev => ({ ...prev, cdpPk: newCdpPk }));                    
                  }}
                >
                  <option value=' '>(Select a CDP)</option>
                  {cdpList.map(cdp => <option key={cdp.cdp_pk} value={cdp.cdp_pk}>{cdp.user_id+"-"+cdp.company}</option>)}
                </select>
              </div>
            )}

            {userGroup === 'CDP' && (
              <div className="mb-4">
                <label htmlFor="Operator_pk_Assigned" className="block text-gray-700 mb-2">Assign this device to an Operator.</label>
                <select
                  className="w-full max-w-xl p-3 border border-gray-300 rounded-lg text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  id="Operator_pk_Assigned"
                  name="Operator_pk_Assigned"
                  value={selectedDevice?.operatorPk || ''}
                  onChange={(e) => setSelectedDevice(prev => ({ ...prev, operatorPk: e.target.value }))}
                >
                  <option value=' '>(Select an Operator)</option>
                  {operatorList.map(op => <option key={op.operator_pk} value={op.operator_pk}>{op.user_id+"-"+op.company}</option>)}
                </select>
              </div>
            )}

            {/* {userGroup !== 'ADMIN' && userGroup !== 'CDP' && userGroup !== 'OPERATOR' && (
              <div className="mb-4">
              <p className="text-gray-700 mb-3">Assign this device to an Operator.</p> 
              <select className="w-full max-w-xl p-3 border border-gray-300 rounded-lg text-gray-700 bg-white">
                <option>OPRDEMO - EYEAI</option>
              </select>
            </div>)} */}
          </div>

          <div className="border-t border-gray-200 pt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Device Portal Login{' '}
              <span className="text-sm text-green-600 font-normal">(by operator)</span>
            </h2>

            {userGroup === 'ADMIN' ? (
              <div className="mb-6">
                <label htmlFor="user-id" className="block text-gray-700 mb-2">Username</label>
                <input
                  readOnly
                  type="text"
                  id="user-id"
                  name="UserID"
                  autoComplete="off"
                  className="w-full max-w-xl px-4 py-3 bg-gray-100 border border-gray-200 rounded text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={ftpId}
                />
              </div>
            ) : (
              <div className="mb-6">
                <span className="text-gray-700 font-semibold">Username: </span>
                <span className="text-green-600 font-semibold">{selectedDevice?.userId}</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="flex items-center justify-between mb-2 gap-3">
                  <span className="text-sm text-gray-600">Password</span>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Generate
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="off"
                    className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
              <div>
                <div className="flex items-center mb-2">
                  <span className="text-sm text-gray-600">Confirm password</span>
                </div>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="off"
                    className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              {passwordPolicyHint}
            </p>

            <div className="flex gap-4">
              <button
                onClick={handleSave}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
              >
                <Check size={20} /> Save
              </button>
              <button
                onClick={handleBack}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentManager;
